import { AppSpecifier, CoreProtocol, MenuItem, View } from "@data-root/types";
import { MenuProps } from "antd";
import { useEffect, useMemo, useState } from "react";
import { DynamicIcon } from "../DynamicIcon";
import { useAppContext, useClientContext, useNavigation } from "@data-root/react";

export const evaluateMenuItem = async (
  core: CoreProtocol,
  appSpecifier: AppSpecifier,
  item: MenuItem
): Promise<MenuItem> => {
  let show = true;
  if (item.show !== undefined)
    show =
      typeof item.show === "boolean"
        ? item.show
        : await core.evaluateFormula<boolean>(item.show, {
            appId: appSpecifier.appId,
            deploymentId: appSpecifier.deploymentId,
          });

  switch (item.type) {
    case "Divider":
    case "View":
      return { ...item, show };

    case "Group":
      return {
        ...item,
        show,
        items: await evaluateMenu(core, appSpecifier, item.items),
      };
  }
};

export const evaluateMenu = async (
  core: CoreProtocol,
  appSpecifier: AppSpecifier,
  menu: MenuItem[]
) => {
  return await Promise.all(
    menu.map((item) => evaluateMenuItem(core, appSpecifier, item))
  );
};

export const transformMenuItem = async (
  callback: (viewId: string) => void,
  viewMap: Record<string, View>,
  item: MenuItem
): Promise<MenuProps["items"][number] | null> => {
  if (typeof item.show !== "boolean") throw new Error("Invalid menu item");

  if (!item.show) return null;

  switch (item.type) {
    case "Divider":
      return { type: "divider" };

    case "View":
      return {
        key: item.viewId,
        icon: <DynamicIcon type={viewMap[item.viewId].icon} />,
        title: viewMap[item.viewId].name,
        label: viewMap[item.viewId].name,
        onClick: () => callback(item.viewId),
      };

    case "Group":
      return {
        key: item.name,
        title: item.name,
        label: item.name,
        children: await transformMenu(callback, viewMap, item.items),
      };
  }
};

export const transformMenu = async (
  callback: (viewId: string) => void,
  viewMap: Record<string, View>,
  menu: MenuItem[]
): Promise<MenuProps["items"]> => {
  return (
    await Promise.all(
      menu.map((item) => transformMenuItem(callback, viewMap, item))
    )
  ).filter((item) => item !== null) as MenuProps["items"];
};

export const useAntdMenu = (callback: (viewId: string) => void) => {
  const { core } = useClientContext();
  const { appSpecifier, menu, views } = useAppContext();
  const { navigation } = useNavigation();

  const [loaded, setLoaded] = useState(false);
  const [antdMenu, setAntdMenu] = useState<MenuProps["items"]>([]);

  const viewMap = useMemo(
    () => Object.fromEntries(views.map((view) => [view.id, view])),
    [views]
  );

  useEffect(() => {
    (async () => {
      setAntdMenu(
        await transformMenu(
          callback,
          viewMap,
          await evaluateMenu(core, appSpecifier, menu)
        )
      );
      setLoaded(true);
    })();
  }, [appSpecifier, callback, core, menu, navigation, viewMap]);

  return { antdMenu, loaded };
};
